import { ISendActionGet } from '@EcamModel/controllers/IPTZCamConfigureRequestHttpController';
import { sp111ConfigureRequestHttpController } from '@controllers/index';
import { useState } from 'react';
import { useQuery } from 'react-query';

export enum SectionsCollapseSP111 {
    Basic = 'basic',
    Others = 'others',
    SP111_OSD = 'SP111_OSD',

    InitialPosition = 'initialPosition',
    ScheduledTasks = 'scheduledTasks',

    AutoTracking_BasicSettings = 'autoTracking_BasicSettings',
    AutoTracking_ScheduleSettings = 'autoTracking_ScheduleSettings',

    SetTheSystemTime = 'setTheSystemTime',
    CurrentSystemTime = 'currentSystemTime',

    ImageAdjustment = 'imageAdjustment',
    ImageEnhancement = 'imageEnhancement',
    DayNightSwitch = 'day_nightSwitch',
    DayNightParameters = 'day_nightParameters',
    Exposure = 'exposure',
    Backlight = 'backlight', 
    WhiteBalance = 'whiteBalance',
    Display = 'display',

    MotionDetection_BasicSettings = 'motionDetection_BasicSettings',
    MotionDetection_ScheduleSettings = 'motionDetection_ScheduleSettings',

    StorageManagement = 'storageManagement',
    RecordSettings = 'recordSettings',
    SnapshotSettings = 'snapshotSettings',

    PrimaryStream = 'primaryStream',
    SecondaryStream = 'secondaryStream',
    TertiaryStream = 'tertiaryStream',
}

export interface LoadingState {
    basicInfoMilesightsSP111: boolean;
    SP111Limit: boolean;
    SP111AutoTracking: boolean;
    SP111MotionDetection: boolean;
    SP111RecordSetting: boolean;
    SP111ScheduledTask: boolean;
    SP111SnapShotSetting: boolean;
    SP111StorageSetting: boolean;
    SP111VideoGeneral: boolean;
    SP111SysDateTime: boolean;
    SP111CameraDayNight: boolean;
    SP111CameraSetting: boolean;
}

type Props = {
    filteredCamera: ISendActionGet;
    cameraSP111: number;
};

export default function useMileSightSP111(props: Props) {
    const [isOpenCollapse, setIsOpenCollapse] = useState<{ [key in SectionsCollapseSP111]: boolean }>({
        basic: true,
        SP111_OSD: false,
        others: false,
        initialPosition: false,
        scheduledTasks: false,
        day_nightSwitch: false,
        storageManagement: false,
        recordSettings: false,
        snapshotSettings: false,
        autoTracking_BasicSettings: false,
        autoTracking_ScheduleSettings: false,
        backlight: false,
        currentSystemTime: false,
        day_nightParameters: false,
        display: false,
        exposure: false,
        imageAdjustment: false,
        imageEnhancement: false,
        motionDetection_BasicSettings: false,
        motionDetection_ScheduleSettings: false,
        primaryStream: false,
        secondaryStream: false,
        setTheSystemTime: false,
        tertiaryStream: false,
        whiteBalance: false,
    });

    // SP111Limit
    const getLimitMilesightsSP111 = async () => {
        if (props?.cameraSP111) {
            const response = await sp111ConfigureRequestHttpController.getSP111Limit(
                props?.filteredCamera as ISendActionGet
            );
            return response;
        }
    };

    const {
        data: SP111Limit,
        error: errorSP111Limit,
        isLoading: isLoadingSP111Limit,
    } = useQuery(['SP111Limit', props?.filteredCamera], getLimitMilesightsSP111, {
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
        enabled: !!props?.filteredCamera,
    });

    // SP111AutoTracking
    const getAutoTrackingMilesightsSP111 = async () => {
        if (props?.cameraSP111) {
            const response = await sp111ConfigureRequestHttpController.getSP111AutoTracking(
                props?.filteredCamera as ISendActionGet
            );
            return response;
        }
    };

    const {
        data: SP111AutoTracking,
        error: errorSP111AutoTracking,
        isLoading: isLoadingSP111AutoTracking,
    } = useQuery(['SP111AutoTracking', props?.filteredCamera], getAutoTrackingMilesightsSP111, {
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
        enabled: !!props?.filteredCamera,
    });

    // SP111MotionDetection
    const getMotionDetectionMilesightsSP111 = async () => {
        if (props?.cameraSP111) {
            const response = await sp111ConfigureRequestHttpController.getSP111EventMotionDetection(
                props?.filteredCamera as ISendActionGet
            );
            return response;
        }
    };

    const {
        data: SP111MotionDetection,
        error: errorSP111MotionDetection,
        isLoading: isLoadingSP111MotionDetection,
    } = useQuery(['SP111MotionDetection', props?.filteredCamera], getMotionDetectionMilesightsSP111, {
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
        enabled: !!props?.filteredCamera,
    });

    // SP111RecordSetting
    const getRecordSettingMilesightsSP111 = async () => {
        if (props?.cameraSP111) {
            const response = await sp111ConfigureRequestHttpController.getSP111RecordSetting(
                props?.filteredCamera as ISendActionGet
            );
            return response;
        }
    };

    const {
        data: SP111RecordSetting,
        error: errorSP111RecordSetting,
        isLoading: isLoadingSP111RecordSetting,
    } = useQuery(['SP111RecordSetting', props?.filteredCamera], getRecordSettingMilesightsSP111, {
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
        enabled: !!props?.filteredCamera,
    });

    // SP111ScheduledTask
    const getScheduledTaskMilesightsSP111 = async () => {
        if (props?.cameraSP111) {
            const response = await sp111ConfigureRequestHttpController.getSP111ScheduledTask(
                props?.filteredCamera as ISendActionGet
            );
            return response;
        }
    };

    const {
        data: SP111ScheduledTask,
        error: errorSP111ScheduledTask,
        isLoading: isLoadingSP111ScheduledTask,
    } = useQuery(['SP111ScheduledTask', props?.filteredCamera], getScheduledTaskMilesightsSP111, {
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
        enabled: !!props?.filteredCamera,
    });

    // SP111SnapShotSetting
    const getSnapShotSettingMilesightsSP111 = async () => {
        if (props?.cameraSP111) {
            const response = await sp111ConfigureRequestHttpController.getSP111SnapShotSetting(
                props?.filteredCamera as ISendActionGet
            );
            return response;
        }
    };

    const {
        data: SP111SnapShotSetting,
        error: errorSP111SnapShotSetting,
        isLoading: isLoadingSP111SnapShotSetting,
    } = useQuery(['SP111SnapShotSetting', props?.filteredCamera], getSnapShotSettingMilesightsSP111, {
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
        enabled: !!props?.filteredCamera,
    });

    // SP111StorageSetting
    const getStorageSettingMilesightsSP111 = async () => {
        if (props?.cameraSP111) {
            const response = await sp111ConfigureRequestHttpController.getSP111StorageSetting(
                props?.filteredCamera as ISendActionGet
            );
            return response;
        }
    };

    const {
        data: SP111StorageSetting,
        error: errorSP111StorageSetting,
        isLoading: isLoadingSP111StorageSetting,
    } = useQuery(['SP111StorageSetting', props?.filteredCamera], getStorageSettingMilesightsSP111, {
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
        enabled: !!props?.filteredCamera,
    });

    const getVideoGeneralMilesightsSP111 = async () => {
        if (props.cameraSP111) {
            const response = await sp111ConfigureRequestHttpController.getSP111VideoGeneral(
                props?.filteredCamera ?? ({} as ISendActionGet)
            );
            return response;
        }
    };

    const {
        data: SP111VideoGeneral,
        error: errorSP111VideoGeneral,
        isLoading: isLoadingMilesightsSP111VideoGeneral,
    } = useQuery(['SP111VideoGeneral', props?.filteredCamera], getVideoGeneralMilesightsSP111, {
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
        enabled: !!props?.filteredCamera,
    });

    // SP111SysDateTime
    const getSysDateTimeMilesightsSP111 = async () => {
        if (props.cameraSP111) {
            const response = await sp111ConfigureRequestHttpController.getSP111SysDateTime(
                props?.filteredCamera ?? ({} as ISendActionGet)
            );
            return response;
        }
    };

    const {
        data: SP111SysDateTime,
        error: errorSP111SysDateTime,
        isLoading: isLoadingMilesightsSP111SysDateTime,
    } = useQuery(['SP111SysDateTime', props?.filteredCamera], getSysDateTimeMilesightsSP111, {
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
        enabled: !!props?.filteredCamera,
    });

    // SP111CameraDayNight
    const getCameraDayNightMilesightsSP111 = async () => {
        if (props.cameraSP111) {
            const response = await sp111ConfigureRequestHttpController.getSP111CameraDayNight(
                props?.filteredCamera ?? ({} as ISendActionGet)
            );
            return response;
        }
    };

    const {
        data: SP111CameraDayNight,
        error: errorSP111CameraDayNight,
        isLoading: isLoadingMilesightsSP111CameraDayNight,
    } = useQuery(['SP111CameraDayNight', props?.filteredCamera], getCameraDayNightMilesightsSP111, {
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
        enabled: !!props?.filteredCamera,
    });

    // SP111CameraSetting
    const getCameraSettingMilesightsSP111 = async () => {
        if (props.cameraSP111) {
            const response = await sp111ConfigureRequestHttpController.getSP111CameraSetting(
                props?.filteredCamera ?? ({} as ISendActionGet)
            );
            return response;
        }
    };

    const {
        data: SP111CameraSetting,
        error: errorSP111CameraSetting,
        isLoading: isLoadingMilesightsSP111CameraSetting,
    } = useQuery(['SP111CameraSetting', props?.filteredCamera], getCameraSettingMilesightsSP111, {
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
        enabled: !!props?.filteredCamera,
    });

    return {
        isOpenCollapse,
        setIsOpenCollapse,

        SP111Limit,
        isLoadingSP111Limit,
        getLimitMilesightsSP111,

        SP111AutoTracking,
        isLoadingSP111AutoTracking,
        getAutoTrackingMilesightsSP111,

        SP111MotionDetection,
        isLoadingSP111MotionDetection,
        getMotionDetectionMilesightsSP111,

        SP111RecordSetting,
        isLoadingSP111RecordSetting,
        getRecordSettingMilesightsSP111,

        SP111ScheduledTask,
        isLoadingSP111ScheduledTask,
        getScheduledTaskMilesightsSP111,

        SP111SnapShotSetting,
        isLoadingSP111SnapShotSetting,
        getSnapShotSettingMilesightsSP111,

        SP111StorageSetting,
        isLoadingSP111StorageSetting,
        getStorageSettingMilesightsSP111,

        SP111VideoGeneral,
        isLoadingMilesightsSP111VideoGeneral,
        getVideoGeneralMilesightsSP111,

        SP111SysDateTime,
        isLoadingMilesightsSP111SysDateTime,
        getSysDateTimeMilesightsSP111,

        SP111CameraDayNight,
        isLoadingMilesightsSP111CameraDayNight,
        getCameraDayNightMilesightsSP111,

        SP111CameraSetting,
        isLoadingMilesightsSP111CameraSetting,
        getCameraSettingMilesightsSP111,
    };
}
